import i18next from 'i18next';
import PropTypes from 'prop-types';
import React from 'react';

import { ProgressBar } from 'deepsight-react-components';

import {
  formatCurrencyDisplay,
  formatPercentageDisplay,
  getCostBarPercentage,
  getProgressBarPercentage,
  isCostBarOverflow,
  isRatioOverflow,
} from '../../utils/format';
import { getChannelProgressTheme } from '../../utils/getChannelProgressTheme';

import {
  AmountOverlay,
  ProgressArea,
  TooltipBody,
  TooltipLabel,
  TooltipRow,
  TooltipValue,
} from '../../styledComponents';

const DISPLAY_MODE_PERCENT = 'PERCENT';

const renderTooltipContent = ({ metrics, numberDecimals, currency }) => {
  const rows = [
    {
      label: i18next.t('GENERAL.COST_EX_TAX'),
      value: formatCurrencyDisplay(metrics.costExTax, currency),
    },
    {
      label: i18next.t('GENERAL.METRIC_MARGIN_EX_TAX_WITHOUT_CURRENCY'),
      value: formatCurrencyDisplay(metrics.marginExTax, currency),
    },
    {
      label: i18next.t('GENERAL.PRICE_EX_TAX'),
      value: formatCurrencyDisplay(metrics.priceExTax, currency),
    },
    {
      label: i18next.t('ADMIN.RECIPES.PERFORMANCES_TOOLTIP_RATIO'),
      value: formatPercentageDisplay(metrics.ratio, numberDecimals),
    },
    {
      label: i18next.t('GENERAL.MARGIN'),
      value: formatPercentageDisplay(metrics.margin, numberDecimals),
    },
  ];

  return (
    <TooltipBody>
      {rows.map((row) => (
        <TooltipRow key={row.label}>
          <TooltipLabel>{row.label}</TooltipLabel>
          <TooltipValue>{row.value}</TooltipValue>
        </TooltipRow>
      ))}
    </TooltipBody>
  );
};

const ChannelBar = ({ channelIndex, metrics, displayMode, currency, maxPriceExTax }) => {
  const numberDecimals = currency?.numberDecimals;
  const isPercentMode = displayMode === DISPLAY_MODE_PERCENT;

  const overflow = isPercentMode
    ? isRatioOverflow(metrics.ratio)
    : isCostBarOverflow(metrics.costExTax, metrics.priceExTax);

  const percentage = isPercentMode
    ? getProgressBarPercentage(metrics.ratio)
    : getCostBarPercentage(metrics.costExTax, metrics.priceExTax, maxPriceExTax);

  const theme = getChannelProgressTheme(channelIndex, overflow);

  const ratioLabel = formatPercentageDisplay(metrics.ratio, numberDecimals);
  const costLabel = formatCurrencyDisplay(metrics.costExTax, currency);

  return (
    <ProgressArea>
      <ProgressBar
        maxPercentage={100}
        percentage={percentage}
        renderTooltipContent={() => renderTooltipContent({ metrics, numberDecimals, currency })}
        theme={theme}
        variation="positive"
        withDot
        withGradient
      />
      {isPercentMode && metrics.ratio != null && (
        <AmountOverlay $percentage={percentage}>{ratioLabel}</AmountOverlay>
      )}
      {!isPercentMode && metrics.costExTax != null && (
        <AmountOverlay $percentage={percentage}>{costLabel}</AmountOverlay>
      )}
    </ProgressArea>
  );
};

ChannelBar.propTypes = {
  channelIndex: PropTypes.number.isRequired,
  currency: PropTypes.object,
  displayMode: PropTypes.string.isRequired,
  maxPriceExTax: PropTypes.number,
  metrics: PropTypes.shape({
    costExTax: PropTypes.number,
    priceExTax: PropTypes.number,
    marginExTax: PropTypes.number,
    ratio: PropTypes.number,
    margin: PropTypes.number,
  }).isRequired,
};

ChannelBar.defaultProps = {
  currency: null,
  maxPriceExTax: null,
};

export default ChannelBar;
